import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  listServiceGroups,
  deleteServiceGroup,
  updateServiceGroupStatus,
} from "../../../../actions/serviceGroupActions";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/shared/Table";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columnsDesktop } from "../columns/ServiceGroupListColumns"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/ServiceGroupListColumnsMobile"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import {
  SERVICEGROUP_DELETE_RESET,
  SERVICEGROUP_UPDATE_RESET,
  SERVICEGROUP_CREATE_RESET,
  SERVICEGROUP_UPDATE_STATUS_RESET,
} from "../../../../constants/serviceGroupConstants";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import CreateServiceGroup from "../sidepanels/CreateServiceGroup";
import EditServiceGroup from "../sidepanels/EditServiceGroup";
import { primary } from "../../../../utils/colors";
import useThemeColors from "../../../../hooks/useThemeColors";
import PageHeader from "../../../../components/layout/PageHeader";
import SearchInput from "../../../../components/input/SearchInput";
import useDebounce from "../../../../hooks/useDebounce";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
import useIsMobile from "../../../../hooks/useIsMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";

const ServiceGroupListNew = () => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const isMobile = useIsMobile();

  // STATE
  const [selectedServiceCount, setSelectedServiceCount] = useState(0);

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // COLORS
  const { whiteMode } = useThemeColors();

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const serviceGroupList = useSelector((state) => state.serviceGroupList);
  const {
    loading,
    error,
    serviceGroups,
    pages,
    totalCount,
    countsByStatus,
    globalTotalCount,
  } = serviceGroupList;

  const serviceGroupCreate = useSelector((state) => state.serviceGroupCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    serviceGroup: createdServiceGroup,
  } = serviceGroupCreate;

  const serviceGroupUpdate = useSelector((state) => state.serviceGroupUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = serviceGroupUpdate;

  const serviceGroupDelete = useSelector((state) => state.serviceGroupDelete);

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = serviceGroupDelete;

  const serviceGroupUpdateStatus = useSelector(
    (state) => state.serviceGroupUpdateStatus
  );
  const {
    loading: loadingUpdateStatus,
    error: errorUpdateStatus,
    success: successUpdateStatus,
  } = serviceGroupUpdateStatus;

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1);
  };

  // FILTERING
  const [isFilterActive, setIsFilterActive] = useState(false);

  const filterConfig = {
    serviceGroups: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: "",
    isConsulting: "",
  });

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setIsFilterActive(true);
    setPage(1);
    localStorage.setItem(
      "filterSettings_serviceGroups",
      JSON.stringify(filters)
    ); // Example for PatientList
    dispatch(
      listServiceGroups(
        slug,
        searchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false, // Assuming `fetchAll` is false by default
        filters.status,
        filters.isConsulting
      )
    );
  };

  // SORTING
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSortChange = (column) => {
    const newSortOrder =
      sortField === column && sortOrder === "asc" ? "desc" : "asc";

    if (sortField !== column || sortOrder !== newSortOrder) {
      // Only dispatch the action if sorting parameters change
      console.log(`Sorting ${column} in ${newSortOrder} order`);

      setSortField(column);
      setSortOrder(newSortOrder);
    }
  };

  // SIDEPANELS
  const [sidePanelModalOpen, setSidePanelOpen] = useState(false);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);

  // CONSTANTS
  const [serviceGroupId, setServiceGroupId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [serviceGroupName, setServiceGroupName] = useState("");

  // HANDLERS

  const deleteServiceGroupHandler = (serviceGroupId) => {
    dispatch(deleteServiceGroup(slug, serviceGroupId));

    if (serviceGroups.length === 1) {
      setPage(page - 1);
    }
  };

  // USEEFFECT

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listServiceGroups(
          slug,
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          false,
          filterParams.status
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    slug,
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    successUpdate,
    successUpdateStatus,
  ]);

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(t("toasts.serviceGroupCreate"), "success", 3000);
      closeSidePanelModal();
    } else if (errorCreate) {
      toastComponent.showToast(
        t("toasts.serviceGroupCreateError"),
        "error",
        3000
      );
    }
    dispatch({ type: SERVICEGROUP_CREATE_RESET });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCreate, errorCreate]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(t("toasts.serviceGroupUpdate"), "success", 3000);
      closeEditSidePanelModal();

      dispatch({ type: SERVICEGROUP_UPDATE_RESET });
    } else if (errorUpdate) {
      toastComponent.showToast(
        t("toasts.serviceGroupUpdateError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdate, errorUpdate]);

  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(t("toasts.serviceGroupDelete"), "success", 3000);

      dispatch({ type: SERVICEGROUP_DELETE_RESET });
    } else if (errorDelete) {
      toastComponent.showToast(
        t("toasts.serviceGroupDeleteError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successDelete, errorDelete]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.serviceGroupUpdateStatus"),
        "success",
        3000
      );

      dispatch({ type: SERVICEGROUP_UPDATE_STATUS_RESET });
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.serviceGroupUpdateStatusError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdateStatus, errorUpdateStatus]);

  // SIDEPANEL HANDLERS

  const openSidePanelModal = () => {
    setSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const openEditSidePanelModal = () => {
    setEditSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeSidePanelModal = () => {
    setSidePanelOpen(false);
    setEditSidePanelOpen(false);
    setServiceGroupId("");
    document.body.classList.remove("no-scroll");
  };
  const closeEditSidePanelModal = () => {
    setEditSidePanelOpen(false);
    setSidePanelOpen(false);
    setServiceGroupId("");
    document.body.classList.remove("no-scroll");
  };

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.on;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.create;
  const hasAccessToEdit =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.update;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.delete;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    console.log("perms", perms);

    if (perms.servicesAndServicesGroups?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.serviceGroups")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching || !serviceGroups;

  // LOGS

  // console.log("loading", loading);
  // console.log("isFetching", isFetching);
  // console.log("isLoading", isLoading);
  // console.log("serviceGroups", serviceGroups);
  // console.log("serviceGroupId", serviceGroupId);

  // console.log("selectedServiceCount", selectedServiceCount);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.serviceGroups")}</title>
        <meta name="description" content={t("pageDescription.serviceGroups")} />
      </Helmet>

      {/* SIDEPANELS START */}
      {!isMobile ? (
        <SidePanel
          open={sidePanelModalOpen}
          close={closeSidePanelModal}
          title={t("serviceGroup.createServiceGroup")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateServiceGroup close={closeSidePanelModal} />
            ) : (
              <UserNoAccess pageName={t("noAccess.serviceGroupCreate")} p="0" />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={sidePanelModalOpen}
          onClose={closeSidePanelModal}
          title={t("serviceGroup.createServiceGroup")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateServiceGroup close={closeSidePanelModal} />
          ) : (
            <UserNoAccess pageName={t("noAccess.serviceGroupCreate")} p="0" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editSidePanelOpen}
          close={closeEditSidePanelModal}
          title={t("serviceGroup.editServiceGroup")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditServiceGroup
              hasEditAccess={hasAccessToEdit}
              close={closeEditSidePanelModal}
              serviceGroupId={serviceGroupId}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editSidePanelOpen}
          onClose={closeEditSidePanelModal}
          title={t("serviceGroup.editServiceGroup")}
          size="full"
        >
          <EditServiceGroup
            hasEditAccess={hasAccessToEdit}
            close={closeEditSidePanelModal}
            serviceGroupId={serviceGroupId}
          />
        </DrawerNew>
      )}
      {/* SIDEPANELS END */}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.serviceGroupDelete")}
          deleteTitle={t("serviceGroup.deleteServiceGroupTitle")}
          deleteName={serviceGroupName}
          warningLabel={
            selectedServiceCount > 0
              ? t("serviceGroup.deleteWarningWithServices", {
                  count: selectedServiceCount,
                })
              : t("serviceGroup.deleteWarning")
          }
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            if (selectedServiceCount > 0) {
              dispatch(
                updateServiceGroupStatus(slug, serviceGroupId, "inactive")
              );
            } else {
              deleteServiceGroupHandler(serviceGroupId);
            }
            setShowDeleteConfirmation(false);
          }}
        />
      )}
      {/* MODALS END */}

      {/* MAIN START */}

      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 6rem", "0rem 0rem 6rem", "2rem 2rem 6rem"]}
        >
          <Box
            w="100%"
            h="100%"
            bg={whiteMode}
            overflow="hidden"
            borderRadius={["0rem", "2rem"]}
          >
            <PageHeader
              label={t("serviceGroup.serviceGroups")}
              buttonHandler={openSidePanelModal}
              buttonLabel={t("serviceGroup.createServiceGroup")}
              filter={
                <></>
                // <TreatmentPlansFilterPopover
                //   onFilterApply={handleFilterApply}
                //   isFilterActive={isFilterActive}
                //   setIsFilterActive={setIsFilterActive}
                //   filterStatusOptions="treatment"
                //   savedFiltersKey="filterSettings_treatmentPlans"
                //   resetFilter={resetFilter}
                //   onResetComplete={() => setResetFilter(false)}
                // />
              }
            />

            <SearchInput
              placeholder={t("serviceGroup.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0.5rem 1rem", "0 2rem"]}
            />

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={serviceGroups}
                      columns={columnsDesktop(
                        slug,
                        dispatch,
                        translate,
                        updateServiceGroupStatus,
                        handleSortChange,
                        setServiceGroupId,
                        setShowDeleteConfirmation,
                        setServiceGroupName,
                        setSelectedServiceCount,
                        openEditSidePanelModal,
                        hasAccessToEdit
                      )}
                      noItemsTitle={t("noItems.noServiceGroups")}
                      noItemsDescription={t("noItems.noServiceGroupsText")}
                      noItemsButtonLabel={t("noItems.noServiceGroupsButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(serviceGroupId) => {
                        console.log("ONROWCLICK", serviceGroupId);
                        setServiceGroupId(serviceGroupId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <>
                {!isLoading ? (
                  <>
                    <Box w="100%">
                      <TableContainerWindowed
                        loading={loading}
                        data={serviceGroups}
                        columns={columnsMobile(
                          slug,
                          dispatch,
                          translate,
                          updateServiceGroupStatus,
                          handleSortChange,
                          isMobile,
                          hasAccessToEdit
                        )}
                        noItemsTitle={t("noItems.noServiceGroups")}
                        noItemsDescription={t("noItems.noServiceGroupsText")}
                        noItemsButtonLabel={t("noItems.noServiceGroupsButton")}
                        noItemsButtonHandler={openSidePanelModal}
                        onRowClick={(serviceGroupId) => {
                          console.log("ONROWCLICK", serviceGroupId);
                          setServiceGroupId(serviceGroupId);
                          openEditSidePanelModal();
                        }}
                        pageSize={pageSize}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        handleSortChange={handleSortChange}
                      />
                      <Flex p="0rem 0rem" w="100%">
                        <Table.Pagination
                          p="1rem 1rem"
                          loading={loading}
                          page={page}
                          pages={pages}
                          pageSize={pageSize}
                          pageSizes={pageSizes}
                          navigate={navigate}
                          setPage={setPage}
                          setPageSize={setPageSize}
                          totalCount={totalCount}
                        />
                      </Flex>
                    </Box>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
              // <Flex
              //   w="100%"
              //   justify="center"
              //   align="flex-start"
              //   p="1rem 1.5rem"
              //   // minH="800px"
              // >
              //   <CardListViewMobile
              //     title={t("serviceGroup.serviceGroups")}
              //     noItemsText={t("noItems.noServiceGroups")}
              //     slug={slug}
              //     data={serviceGroups}
              //     loading={loading}
              //     sortField={sortField}
              //     sortOrder={sortOrder}
              //     onSortChange={handleSortChange}
              //     page={page}
              //     pages={pages}
              //     pageSize={pageSize}
              //     pageSizes={pageSizes}
              //     setPage={setPage}
              //     setPageSize={setPageSize}
              //     totalCount={serviceGroups?.length || 0}
              //     navigate={navigate}
              //     t={t}
              //   >
              //     {serviceGroups?.map((serviceGroup) => (
              //       <ServiceGroupCardMobile
              //         key={serviceGroup._id}
              //         serviceGroup={serviceGroup}
              //       />
              //     ))}
              //   </CardListViewMobile>
              // </Flex>
            )}
          </Box>
        </Flex>
      </Box>
      {/* MAIN END */}
    </>
  );
};

export default ServiceGroupListNew;
